// Тонкая обёртка над fetch к /api/*. cabinet-app использует web-JWT из localStorage
// (Authorization: Bearer), а НЕ cookie-session. Refresh-токен — в httpOnly cookie,
// обновляется отдельно через /api/v1/auth/refresh (credentials:'include').

// Только тип и только отсюда: канон формы истории флота живёт рядом с её
// нарезкой (`map/fleetHistory.ts`), потому что вторая копия формы разъехалась бы
// с первой ровно так же, как когда-то разъехались палитры карты. Импорт
// типовой — на выполнении его нет, кольца тоже.
import type { FleetHistory } from './map/fleetHistory'
import type { BanProps } from './map/bans'
import type { CalendarDay } from './imagery/datasetCalendar'
import type { AisFix, DatasetMark, DatasetRecord, DatasetScene } from './imagery/datasetLayer'
import { getLang } from './i18n'

export const TOKEN_KEY = 'cabinet.token'

export class ApiError extends Error {
  status: number
  /** `detail` ответа КАК ЕСТЬ — обычно `{code, …}` кабинетного API.
   *
   *  Раньше сюда доезжала только строка, а объект отбрасывался: сервер отдаёт
   *  ошибку КОДОМ (`{"detail": {"code": "region_forbidden"}}`), и вызывающему
   *  оставался безымянный «HTTP 403». Плашке нужен именно код — слова к нему
   *  подставляет каталог (`errorText`), а не сервер. */
  detail: unknown
  constructor(status: number, message?: string, detail?: unknown) {
    super(message ?? `HTTP ${status}`)
    this.status = status
    this.detail = detail
    this.name = 'ApiError'
  }
}

/** Собирает query-string, пропуская undefined/null. Возвращает '' если параметров нет. */
export function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const parts: string[] = []
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
  }
  return parts.length ? `?${parts.join('&')}` : ''
}

/** Заголовок авторизации из токена (пустой объект, если токена нет). */
export function authHeaders(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

// Синглтон тихого refresh: параллельные 401 ждут один запрос к /auth/refresh,
// а не поднимают лавину. Возвращает новый access-токен или null.
let refreshPromise: Promise<string | null> | null = null

async function doRefresh(): Promise<string | null> {
  try {
    const res = await fetch('/api/v1/auth/refresh', { method: 'POST', credentials: 'include' })
    if (!res.ok) return null
    const data = await res.json()
    const newToken: string | undefined = data?.access_token
    if (!newToken) return null
    try {
      localStorage.setItem(TOKEN_KEY, newToken)
    } catch {
      /* ignore */
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('cabinet:token-refreshed', { detail: { token: newToken, user: data.user } }),
      )
    }
    return newToken
  } catch {
    return null
  }
}

function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

async function request<T>(url: string, init: RequestInit = {}, retried = false): Promise<T> {
  const headers: Record<string, string> = {
    ...authHeaders(getToken()),
    ...(init.headers as Record<string, string> | undefined),
  }
  const res = await fetch(url, { ...init, headers })
  if (!res.ok) {
    // Access-токен короткоживущий (30 мин); на 401 сначала пробуем тихий refresh
    // по httpOnly-cookie (30 дней) и повторяем запрос ОДИН раз с новым токеном.
    // Логаут — только если refresh не удался.
    if (res.status === 401 && !retried && !url.includes('/auth/refresh')) {
      const newToken = await refreshAccessToken()
      if (newToken) {
        return request<T>(url, init, true)
      }
      try {
        localStorage.removeItem(TOKEN_KEY)
      } catch {
        /* ignore */
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('cabinet:unauthorized'))
      }
    }
    let raw: unknown
    let detail: string | undefined
    try {
      const body = await res.json()
      raw = body?.detail
      detail = typeof raw === 'string' ? raw : undefined
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, detail, raw)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export async function getJson<T>(url: string): Promise<T> {
  return request<T>(url)
}

/**
 * Ответ БАЙТАМИ (выгрузка NetCDF).
 *
 * Отдельно от `request`, потому что тот разбирает JSON: файл на пару мегабайт
 * через `res.json()` не приедет вовсе. Обновление токена повторяем той же
 * одной попыткой — иначе долгая сборка файла на протухшем access-токене
 * возвращала бы человеку 401 вместо данных.
 */
export async function getBytes(url: string, retried = false): Promise<Uint8Array<ArrayBuffer>> {
  const res = await fetch(url, { headers: authHeaders(getToken()) })
  if (!res.ok) {
    if (res.status === 401 && !retried && (await refreshAccessToken())) {
      return getBytes(url, true)
    }
    let raw: unknown
    try {
      raw = (await res.json())?.detail
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, typeof raw === 'string' ? raw : undefined, raw)
  }
  return new Uint8Array(await res.arrayBuffer())
}

export async function postJson<T>(url: string, body?: unknown): Promise<T> {
  return request<T>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
}

export async function putJson<T>(url: string, body?: unknown): Promise<T> {
  return request<T>(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
}

export async function patchJson<T>(url: string, body?: unknown): Promise<T> {
  return request<T>(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
}

export async function deleteJson<T>(url: string): Promise<T> {
  return request<T>(url, { method: 'DELETE' })
}

// === Типы cabinet API (наполняются по фазам) ===

export interface OrgSummary {
  org_id: number
  name: string
  role: string
  features?: Record<string, unknown>
  /** Что кабинету продано — считает сервер (`service_flags`). */
  services?: Record<string, boolean>
}

// === «Отчётность → Пропуски АИС» (`/cabinet/reports/ais-gaps`) ===

/** Точка трека: секунды эпохи UTC, широта, долгота. */
export type AisGapPoint = [number, number, number]

export interface AisGapVessel {
  mmsi: number
  name: string | null
  status: 'ok' | 'no_points' | 'not_in_source' | 'error'
  points: number
  error: string | null
  track: AisGapPoint[]
}

export type AisGapVerdict = 'only_vessel' | 'area' | 'no_neighbors' | 'unchecked'

export interface AisGap {
  no: number
  mmsi: number
  start: number
  end: number
  hours: number
  edge: boolean
  before: [number, number] | null
  after: [number, number] | null
  port: { name: string; km: number } | null
  check: { verdict: AisGapVerdict; checked: number; visible: number; reason: string | null } | null
  /** Соседи с сигналом во время пропуска. */
  neighbors: { mmsi: number; km: number }[]
}

export interface AisGapResult {
  version: number
  window: [number, number]
  threshold_h: number
  radius_km: number
  vessels: AisGapVessel[]
  gaps: AisGap[]
  neighbors: Record<string, { name: string | null; track: AisGapPoint[] }>
}

export interface AisGapJob {
  id: number
  title: string | null
  /** Номер источника (3 — файлы партнёра), 0 — общий пропуск; имени кабинет не получает. */
  source: 0 | 1 | 2 | 3
  status: 'queued' | 'running' | 'done' | 'failed'
  done: number
  total: number
  vessels: number
  t0: string
  t1: string
  threshold_h: number
  created_at: string
  error: string | null
  gaps: number | null
}

/** Источник отчёта: его задание, статус и окно (у источника с задержкой — короче). */
export interface AisGapSourceRun {
  source: 0 | 1 | 2 | 3
  id: number
  status: AisGapJob['status']
  done: number
  total: number
  gaps: number | null
  t1: string
  error: string | null
}

/** Отчёт кабинета — группа заданий, по одному на источник. */
export interface AisGapGroup {
  id: number
  title: string | null
  vessels: number
  t0: string
  t1: string
  threshold_h: number
  created_at: string
  sources: AisGapSourceRun[]
}

export interface AisGapJobs {
  groups: AisGapGroup[]
  max_vessels: number
  max_days: number
  radius_km: number
  /** Где кончаются данные каждого источника (ISO, UTC). */
  available_until: Partial<Record<1 | 2, string>>
  /** Источники, которые сейчас не поставятся. */
  unavailable: (1 | 2)[]
}

export interface AisGapReport extends AisGapJob {
  mmsis: string[]
  /** Этап «суда рядом с пропусками»: пусто — его нет или он не начался. */
  neighbors_done: number | null
  neighbors_total: number | null
  /** Чья сеть дала соседей — номер источника; null — смешанная или старый ответ. */
  neighbors_from?: number | null
  result: AisGapResult | null
}

export interface AisGapCreate {
  text: string
  t_from: string
  t_to: string | null
  threshold_h: number
  title: string | null
}

/** Сводка раздела «Рынок»: источники, с данными, позиции с ценами. */
export interface PricesSummary {
  sources: number
  live: number
  items: number
  last_published: string | null
}

export interface CustomsSpecies {
  code: string
  name_ru: string
  name_en: string
  /** Латынь видов, которые покрывает групповой код таможни; у вида — null. */
  covers: string[] | null
}

export interface CustomsItem {
  item_key: string; species: string; processing: string; thermal: string
  /** Размер позиции («25+», «1-2кг»); у таможенных кодов — null. */
  size_grade?: string | null
}

export interface CustomsCell {
  item_key: string
  country: string
  /** Рынок внутри страны (Токио: Toyosu, Adachi, Ota); пусто — вся страна. */
  city: string
  /** ISO3 партнёра; пусто — итог по всем странам. */
  partner: string
  /** null — масса не публикуется (Токио не делит объём по сортам), цена — простое среднее. */
  tonnes: number | null
  usd_per_kg: number | null
  rub_per_kg: number | null
}

/** «Рынок → Аналитика»: звено цепочки — средняя за 12 месяцев. */
export interface ChainRow {
  link: string
  item_key: string
  species: string
  /** позиция группы (минтай — в «Рыбе» Росстата), а не самого вида */
  group: boolean
  processing: string
  thermal: string
  country: string
  city: string
  partner: string
  rub_per_kg: number
  usd_per_kg: number | null
  points: number
  last: string
}

export interface FxRow {
  year: number
  vs: number
  rub_change_pct: number
  usd_change_pct: number
  fx_change_pct: number
  fx_share: number | null
}

export interface PricesOverview {
  species: string
  chain: ChainRow[]
  main: { link: string; item_key: string; country: string; partner: string } | null
  /** месяц → цена месяца / средняя года; null — полных лет меньше двух */
  seasonality: Record<string, number> | null
  fx: FxRow[]
  supply: { quota: { year: number; quota_t: number; caught_t: number }[] }
  /** пошлины импортёров для России (член ВТО); import_quota — ввоз только по квоте */
  tariffs: { importer: string; code: string; mfn_text: string; mfn_pct: number | null;
    import_quota: boolean; valid_from: string; item_key: string; processing: string;
    thermal: string }[]
}

/** «Рынок → Розница»: позиции Росстата на одной территории, ₽ за кг. */
export interface RetailItem {
  item_key: string
  species: string
  processing: string
  thermal: string
  pack: string | null
  name_ru: string
  name_en: string
}

export interface RetailSeries {
  items: RetailItem[]
  months: { item_key: string; month: string; rub_per_kg: number }[]
  /** Год — простое среднее месяцев (`months` — сколько их было). */
  years: { item_key: string; year: number; rub_per_kg: number; months: number }[]
  /** ориентир на следующий месяц (тренд 6 мес.) — только по России: на территориях
   *  он не прошёл гейт против наивного прогноза */
  outlook: { item_key: string; month: string; rub_per_kg: number }[]
}

/** Розница сетей (Tonusmeal), внутренний источник: строка корзины × позиция, медиана по сетям. */
export interface ChainsRow extends Omit<RetailItem, 'pack'> {
  stores: Record<string, number>
  median: number
  min: number
  max: number
  points: number
}

export interface ChainsSeries {
  city: string
  day: string
  stores: string[]
  lines: { code: string; name: string; rows: ChainsRow[] }[]
}

export interface CustomsSeries {
  items: CustomsItem[]
  months: (CustomsCell & { month: string })[]
  /** `annual` — годовая точка источника (Китай): месяцев за ней нет, `months` — null. */
  years: (CustomsCell & { year: number; months: number | null; annual: boolean })[]
  coverage: {
    source: string
    country: string
    last_month: string | null
    /** Период последней точки: у годового источника — 'year'. */
    period: 'day' | 'week' | 'month' | 'year' | null
    published_at: string | null
  }[]
}

export interface BBox {
  lat_min: number
  lat_max: number
  lon_min: number
  lon_max: number
}

export interface PrepareBody {
  bbox: BBox
  date: string
  layers: string[]
  species_id?: number | null
  depth?: number
  kind?: 'raw' | 'model' | 'upwelling'
  /** Ставить ли попутные работы (позиции судов района). По умолчанию да; второй
   *  prepare внутри одной цепочки шлёт false, чтобы не плодить дубль задания. */
  side_jobs?: boolean
  /** Рисовать только показанную глубину, а не всю пирамиду из семнадцати
   *  уровней. Просит подготовка ролика: ей нужны все сутки окна и один срез в
   *  каждых. Кнопке «Загрузить» пирамида нужна — по ней ходят ползунком. */
  depth_only?: boolean
}

export interface DeltaItem {
  param: string
  missing_dates: string[]
  complete: boolean
}

export interface PrepareResponse {
  task_id: string
  manifest: DeltaItem[]
  needs_download: boolean
  needs_recompute: boolean
  estimated_tiles: number
  /** Фоновое обновление позиций судов района. null — все квадраты свежее 3 часов. */
  fleet_job_id?: string | null
  /** Точечный добор позиций по карточкам MarineTraffic (см. src/map/cardRefresh.ts). */
  card_job_id?: number | null
  /** Нажатие присоединилось к открытому заданию того же кадра: вторая полоска
   *  не заводится. */
  card_job_joined?: boolean
}

/** Что уже нарисовано по виду и дате (см. models/probabilityLayer.ts). */
export interface RasterStatus {
  status?: string
  available_layers?: Array<{ layer_depth?: string; tile_count?: number }>
  /** Метка последней отрисовки: без неё браузер сутки держит старые тайлы. */
  tiles_version?: string | null
}

/** Прогресс точечного добора позиций по карточкам — тип живёт рядом с опросом. */
import type { CardJobStatus } from './map/cardRefresh'
export type { CardJobStatus }

/** Статус фонового обновления позиций судов (см. src/map/fleetRefresh.ts). */
export interface FleetRefreshResponse {
  done: boolean
  error: string | null
  tiles: number
  updated: number
  queued: number
  /** Оценка опознания поставленных в очередь судов, сек (N × 7 c: карточки
   *  MarineTraffic берутся строго по одной). Первый заход в район — часы. */
  resolve_eta_sec: number
}

export interface ProgressResponse {
  task_id: string
  status: string
  progress_percent: number
  current_step: string | null
  error_message: string | null
}

export interface Availability {
  layer: string
  date: string
  depth: number
  data_present: boolean
  tiles_ready: boolean
  is_static: boolean
  // Метка версии отрендеренных тайлов → подставляется в URL тайла, иначе
  // браузер сутки отдаёт старую картинку из кэша (max-age=86400, URL прежний).
  tiles_version?: string | null
}

/** Палитра слоя: опоры значений и цвета на них.
 *
 *  Приезжает ЧИСЛАМИ, а не именем цветовой карты. Имя было индиректностью:
 *  клиент держал свой словарь «имя → градиент» и рисовал его от руки, из-за
 *  чего легенда совпадала с картинкой лишь приблизительно. Из чисел собирается
 *  и градиент легенды, и шкала глубин изобат, и образец на чипе слоя.
 *
 *  У бренд-домена цвета СВОИ, а опоры те же: границы шкалы выбраны
 *  океанографией и обязаны значить на всех доменах одно и то же. */
export interface LayerPalette {
  stops: number[]
  colors: number[][]
  /** Шкала полосами, а не градиентом (туман: чисто / дымка / туман). */
  discrete: boolean
}

export interface LayerCatalogItem {
  id: string
  /** Код единицы (`mg_per_m3`, `m_per_s`, `index`); написание — в каталоге фронта.
   *  Пустая строка — величины нет вовсе (фаза Луны). Подписи слоя здесь нет:
   *  `id` — код, слова берутся из `layers.<id>`. */
  unit: string
  /** Нет у слоёв без заливки (фаза Луны, землетрясения). */
  palette?: LayerPalette
  has_depth: boolean
  kind: string
  overlay: boolean
  render: string
  /** Семья слоёв: 'upwelling' у механизмов вертикального движения воды,
   *  'hsi' у подразделов «Комфортной среды».
   *  Витрина строит список механизмов ИЗ КАТАЛОГА, а не из своего перечня. */
  group?: string
  /** Виды, которые слой обслуживает. Приезжает, ТОЛЬКО когда набор ограничен
   *  (сеть скоплений обучена не всем видам); отсутствие поля значит «слой не
   *  про виды», а не «ни одного вида». Список ОДИН на систему и живёт на
   *  сервере: разложив его по витрине и бэкенду, мы получили бы подраздел,
   *  который витрина показывает, а сервер не обслуживает. */
  species?: number[]
  /** Районы, где слой имеет смысл (коды `ref_region`). Приезжает, ТОЛЬКО когда
   *  набор ограничен (лёд — холодные моря); отсутствие значит «везде». */
  regions?: string[]
}

/** Ответ `GET /cabinet/layers`: слои по правам кабинета + признак «Показ гидрологии».
 *
 *  Признак едет ОТДЕЛЬНЫМ полем, а не выводится из состава списка: тот же
 *  список режет `allowed_layers` по другой причине, и одно от другого по дыре в
 *  нём не отличить. Что с признаком делает витрина — `src/hydrology.ts`. */
export interface LayerCatalog {
  layers: LayerCatalogItem[]
  hydrology: boolean
}

/** Одно место нейрокарты: «сюда стоит сходить, и вчера тут никого не было». */
export interface NnMark {
  /** Место в двадцатке, 1 — лучшее. */
  rank: number
  lat: number
  lon: number
  /** Средний ранг поля по ансамблю (0…1): им и выбраны места. */
  field_rank: number
  /** Логарифм интенсивности — то, чем закрашена карта. Может не приехать у
   *  отметок, посчитанных до появления заливки. */
  log_intensity?: number
}

/** Ответ `GET /cabinet/nn/marks`.
 *
 *  `present` отличает «не считали» от «мест не нашлось»: вчерашняя окрестность
 *  могла накрыть всю область, и пустой список тогда — законный ответ, а не
 *  отсутствие прогона. Свернув оба случая, мы объявили бы одно другим. */
export interface NnMarks {
  species_id: number
  date: string
  present: boolean
  marks: NnMark[]
  provenance: {
    /** Снимков в ансамбле: разброс между ними на запечатанном окне 3,5…6,8 %. */
    members: number | null
    /** Точек АИС вчерашнего промысла — из них и построена маска. */
    yesterday_points: number | null
    /** Сколько клеток осталось под выбор и сколько их в области обучения:
     *  двадцатка без этих чисел непроверяема — маска могла закрыть почти всё. */
    cells_free: number | null
    cells_domain: number | null
    new_radius_cells: number | null
    top_n: number | null
    calculated_at: string | null
    /** На сколько суток вперёд от дня расчёта: 1 и 2 — прогноз, 0 — сегодня. */
    lead_days?: number | null
  } | null
}

/** Строка ответа `/cabinet/upwelling/mechanisms`. */
export interface UpwMechanismState {
  mechanism: string
  unit: string
  signed: boolean
  has_depth: boolean
  data_present: boolean
  /** Горизонты, на которых поле посчитано (пусто у безглубинных). */
  depths: number[]
}

export interface GeoJSONFeatureCollection {
  type: 'FeatureCollection'
  features: Array<{
    type: 'Feature'
    geometry: { type: string; coordinates: number[] }
    properties: Record<string, unknown>
  }>
}

/** Строка выдачи поиска судна (совпадает с VesselFocus на карте). */
export interface VesselSearchHit {
  mmsi: string
  name: string | null
  /** Русское название из Регистровой книги РМРС; null — судна в ней нет. */
  name_ru: string | null
  flag_state: string | null
  company: string | null
  lat: number
  lon: number
  last_position_utc: string | null
  /** Вид промысла судна, если его категория в реестре NPFC однозначна;
   *  null — категории нет либо она указывает сразу на несколько видов. */
  species_id: number | null
  /** Реестр NPFC — карточка найденного судна показывает его так же, как
   *  карточка точки слоя флота: тип корпуса, орудие лова и цели промысла. */
  rfmo_orgs: string[] | null
  rfmo_type: string | null
  rfmo_gear: string | null
  /** Имена видов-целей ОБОИМИ языками: это данные (их правит админка), а не
   *  подписи интерфейса, и словаря кодов ASFIS у витрины нет. */
  rfmo_target_ru: string[] | null
  rfmo_target_en: string[] | null
  /** Ход (уз) и курс (°) из последнего фикса АИС. */
  sog: number | null
  cog: number | null
}

// === Лагранжев снос («По течениям») ===
// Свойства фичи сноса. Общие для трека (LineString) и снесённой точки (Point):
// kind различает их. source — косяк org ('cluster') или факт вылова ('catch').
// status — накопительная модель: active (доведён до последних течений) /
// beached (уткнулся в берег) / gap (дальше течений ещё нет, дозаполнится).
// 'static' — донный вид (краб): течением не сносится, точка стоит там, где её
// отметили. Отдельный статус, потому что active/beached описывают снос, а gap —
// «течений ещё нет»; ни один из них не объяснил бы неподвижную точку.
export type DriftStatusKind = 'active' | 'beached' | 'gap' | 'static'
export interface DriftProps {
  source: 'cluster' | 'catch'
  ref: number                     // id исходной точки (косяк/вылов) — связь трек↔маркер
  species_id: number
  observed_date: string           // дата наблюдения (YYYY-MM-DD)
  origin: [number, number]        // точка РЕГИСТРАЦИИ (отметка/операция вылова).
                                  // Не первая точка трека: тот начинается с первого
                                  // дня, на который есть течения, — он может быть
                                  // сильно позже отметки.
  last_date: string               // дата ПОСЛЕДНЕЙ точки трека — якорь индексации.
                                  // Считать от observed_date нельзя: трек начинается
                                  // с первого дня, на который есть течения.
  age_days: number                // возраст = currents_through − observed_date (сут)
  status: DriftStatusKind         // active / beached / gap / static
  kind: 'track' | 'position'      // 'track' = LineString, 'position' = финальный Point
  size_class?: 'small' | 'medium' | 'large' | null  // для косяков
  catch_tons?: number | null      // для фактов вылова
  // Единая ступень размера маркера на оба источника: у косяка это size_class,
  // у вылова — градация от эталона (доля от P95 самой подробной ступени
  // лестницы с n≥20, миграция 247). null — эталона не нашлось; подменять его
  // «малым» нельзя, это не слабый улов, а отсутствие данных.
  grade?: 'small' | 'medium' | 'large' | null
  // Чем мерили — без этого «большой» необъяснимый ярлык.
  capacity_level?: number | null  // 1 судно×вид×орудие … 4 весь флот
  capacity_n?: number | null      // размер выборки эталона
  capacity_ratio?: number | null  // catch_tons / P95 эталона
  capacity_label?: string | null  // «БМРТ с орудием «Атлантика-970» на «сельди»»
  // Ансамбль возмущений: радиусы неопределённости (км) по дням трека (выровнены
  // с координатами) + финальный σ. null у старых строк (посчитаны до ансамбля).
  uncertainty_km?: number[] | null
  sigma_current_km?: number | null
  // Точки вылова скрыты настройкой кабинета (`catch_points_visible`), и место
  // тем же признаком огрублено до узла сетки — это ОДНО состояние: маркер по
  // огрублённой точке выглядел бы точным местом, поэтому не рисуется вовсе.
  // Остаётся ореол области; линия сноса сдвинута тем же вектором. Признак
  // приходит с бэкенда: по координатам его не угадать.
  anonymized?: boolean
  // Второе слагаемое радиуса ореола: погрешность показанного центра из-за
  // огрубления (полдиагональ ячейки, 6–8 км). null — не огрубляли.
  place_error_km?: number | null
  // Проставляются срезом на выбранный день (sliceDriftCollection), не бэкендом.
  // Словарь разведён: НАЧЕРТАНИЕ линии = качество данных, ЯРКОСТЬ = акцент перемотки.
  phase?: 'fact' | 'forecast'     // линия: до сегодня (сплошная) / после (пунктир)
  segment?: 'accent' | 'context'  // линия: отрезок сегодня↔день / остальной трек
  // точка: выбранный день (яркая) / якорь «сегодня» (приглушённый) /
  // отметка регистрации (бледная, «откуда снесло»)
  role?: 'day' | 'today' | 'origin'
  frozen?: boolean                // точка: день позже конца трека → полый маркер
  radius_km?: number | null       // радиус ореола выбранного дня (срез, не бэкенд)
}

// Мета-сводка ответа сервиса сноса (накопительная модель).
export interface DriftMeta {
  species_id: number
  window_days: number             // окно-фильтр отображения (1..180)
  currents_through: string | null // дата последних доступных течений (null — течений нет)
  shown: number                   // сколько точек попало в окно
  active: number                  // доведены до последних течений
  beached: number                 // уткнулись в берег
  gap: number                     // ждут свежих течений (дозаполнятся)
  // Места вылова огрублены — легенда обязана это сказать, иначе пропавшие
  // маркеры читаются как поломка данных.
  catch_anonymized?: boolean
  // Настройка кабинета: ореол погрешности скрыт. Легенда молча убирает свою
  // строку — подпись к тому, чего в кадре нет, это враньё.
  catch_halo_hidden?: boolean
}

// FeatureCollection сноса: смешанная коллекция линий-треков и снесённых точек.
export interface DriftCollection {
  type: 'FeatureCollection'
  features: Array<GeoJSON.Feature<GeoJSON.LineString | GeoJSON.Point, DriftProps>>
  meta: DriftMeta
}

// Стрелка направления течения: точка + курс «куда течёт» (градусы по часовой
// от севера, ровно как ждёт icon-rotate) + модуль скорости для длины стрелки.
export interface ArrowProps {
  bearing: number
  speed: number
}

export interface FleetCollection extends GeoJSONFeatureCollection {
  meta?: { shown: number; truncated: boolean }
}

// Трек судна: линии-сегменты операций (kind='operation'). В meta — почему пусто
// и за какой период разметка у судна вообще есть.
export interface VesselTrackCollection extends GeoJSON.FeatureCollection {
  meta: {
    count: number
    truncated: boolean
    reason: string | null
    mmsi?: string
    hours?: Record<string, number>
    available_from?: string | null
    available_to?: string | null
  }
}

// === Живые AIS-позиции судов («Флот» на «Моделях») ===
// Единый источник — таблица ais_vessel (коллектор AISStream + enrichment). Точка
// красится по возрасту фикса: age_h — часы с последней позиции, stale — старше
// суток. last_position_utc null → age_h null (позиции не было, не «устарела»).
export interface VesselProps {
  mmsi: string | null
  name: string | null
  flag_state: string | null
  company: string | null           // юр.лицо (company_key) или null
  rfmo_orgs: string[] | null       // реестры, где судно значится: {'NPFC'}, {'NEAFC'} или оба
  rfmo_type: string | null         // тип корпуса по реестру (часто «not specified»)
  rfmo_gear: string | null         // орудие лова, сырое: «Midwater trawls (nei) (TM)»
  rfmo_target: string[] | null     // цели промысла кодами ({'SAP',...}); сайровик = 'SAP'
  // Те же цели именами видов, обоими языками: коды ASFIS человеку не читаются,
  // а словарь живёт в БД (ref_asfis_species) и правится админкой.
  rfmo_target_ru: string[] | null
  rfmo_target_en: string[] | null
  sog: number | null               // скорость (узлы)
  cog: number | null               // курс над грунтом (градусы)
  heading: number | null
  last_position_utc: string | null // ISO-время последнего фикса
  age_h: number | null             // возраст позиции в часах (null — нет данных)
  stale: boolean                   // age_h > 24
}

export interface VesselCollection {
  type: 'FeatureCollection'
  features: Array<GeoJSON.Feature<GeoJSON.Point, VesselProps>>
  meta?: { shown: number }
}

export interface ArrowsCollection {
  type: 'FeatureCollection'
  features: Array<GeoJSON.Feature<GeoJSON.Point, ArrowProps>>
  // truncated — только у течений (выборка из БД с потолком); у придонных стрелок
  // апвеллинга источник — файл артефакта, обрезать по потолку там нечего.
  meta: { shown: number; truncated?: boolean; step_deg?: number }
}

/** Изобаты: одна фича на глубину (MultiLineString), свойство depth в метрах. */
export interface IsobathCollection {
  type: 'FeatureCollection'
  features: Array<GeoJSON.Feature<GeoJSON.MultiLineString | GeoJSON.LineString, { depth: number }>>
  /** `step_m` — шаг, которым НАРИСОВАНО: на обзорных зумах сервер огрубляет
   *  запрошенный (лестница `cabinet_isobaths.STEP_BY_ZOOM`). Витрина обязана
   *  сказать об этом вслух — молча подменённый шаг читается как враньё
   *  подписи. Полей нет вовсе, когда шаг не задан (пятёрка апвеллинга). */
  meta?: {
    zoom: number
    tolerance_deg: number
    step_m?: number
    step_requested_m?: number
    min_step_m?: number
  }
}

/** Рубежи экономических зон: одна фича на рубеж, свойства — обе его стороны,
 *  тип и документ-основание.
 *
 *  `meta.attribution` и `meta.disclaimer_key` приезжают С ОТВЕТОМ, а не зашиты
 *  во фронте: лицензия CC-BY принадлежит ДАННЫМ, и подпись, живущая отдельно от
 *  них, пережила бы смену источника и стала бы ложной. Оговорка — КЛЮЧ каталога,
 *  а не фраза: готовый текст втащил бы язык кабинета в ответ ручки. */
export interface EezLineProperties {
  line_id: number | null
  line_type: string | null
  line_name: string | null
  sovereign1: string | null
  territory1: string | null
  sovereign2: string | null
  territory2: string | null
  source: string | null
  doc_date: string | null
  /** Чем строка отличается от источника и почему; `null` — не правлена. */
  override_note: string | null
}

/** Промысловые зоны и подзоны вьюпорта. Свойства едут КОДАМИ и данными —
 *  `map_code` подписывается на карте, `osm_code` нужен для сверки донесений. */
export interface DistrictProperties {
  code: string
  map_code: string
  name: string
  zone: string | null
  kind: string
  osm_code: number | null
  /** Что в этом районе решено НАМИ, а не приказом: выведенные точки, спорные
   *  прочтения. Без метки такая граница читается как утверждение приказа. */
  note: string | null
  label: [number, number]
}

export interface DistrictCollection {
  type: 'FeatureCollection'
  features: Array<
    GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon, DistrictProperties>
  >
  meta?: { attribution: string; disclaimer_key: string }
}

/** Тип дна по картам ГГК-1000 (`/cabinet/substrate`). */
export interface SubstrateCollection {
  type: 'FeatureCollection'
  features: Array<
    GeoJSON.Feature<
      GeoJSON.Polygon | GeoJSON.MultiPolygon,
      { sheet: string; code: string; label: string; layer: 'lithology' | 'outcrop'; class5: number }
    >
  >
  meta?: { attribution: string; disclaimer_key: string }
}

/** Навигационное предупреждение NGA (`/cabinet/navwarn`). Род — код, слово
 *  подставляет каталог (`navwarn.kind.<код>`). */
export interface NavwarnMessage {
  id: number
  navarea: 'PAC' | 'XII' | 'ARC'
  number: number
  year: number
  kind: string
  regions: string[]
  issued: string | null
  gone: string | null
}

export interface NavwarnShapeProps extends NavwarnMessage {
  label: string | null
  shape: 'polygon' | 'line' | 'corridor' | 'circle' | 'point'
  radius_m: number | null
}

export interface NavwarnCollection {
  type: 'FeatureCollection'
  features: Array<GeoJSON.Feature<GeoJSON.Geometry, NavwarnShapeProps>>
  messages: NavwarnMessage[]
  meta?: {
    date: string
    /** Сводка → «в силе на» (UTC). */
    memos: Record<string, string | null>
    attribution: string
    disclaimer_key: string
  }
}

export interface NavwarnFull extends NavwarnMessage {
  text: string
  has_coords: boolean
  shapes_parsed: boolean
  first_seen: string | null
}

/** Раздел «Квоты» (`/cabinet/quotas`): распределение по приказу, без вылова. */
export type QuotaKind = 'industrial' | 'coastal' | 'crab' | 'invest'
export interface QuotaZone {
  code: number
  name: string
  tons: number
  holders: number
  kinds: Partial<Record<QuotaKind, number>>
  /** Объём вида квоты по ОДУ в этом районе — сверка распределения (только у выбранного вида). */
  tac?: Partial<Record<QuotaTacKind, number>>
}
/** Виды квот ОДУ (`quota_tac_split.quota_kind`). */
export type QuotaTacKind = 'industrial' | 'coastal' | 'invest_industrial' | 'invest_crab'
  | 'international' | 'science' | 'education' | 'aquaculture' | 'indigenous' | 'recreational'
/** Правка, которой нет в цепочке редакции: не скачана или не найдена вовсе. */
export interface QuotaChainGap { no: string; date: string; reason: 'not_downloaded' | 'absent' }
export interface QuotaSharesResponse {
  basins: string[]
  basin: string
  gaps?: Array<{ order: string; date: string; gaps: QuotaChainGap[] }>
  lists: Array<{
    order: string; list: string | null; vid: string; zone: string; holders: number; total: number
    rows: Array<{ name: string; inn: string | null; share: number; object: string | null }>
  }>
}
export interface QuotaTacResponse {
  basins: string[]
  basin: string
  years: number[]
  year: number | null
  kinds: QuotaTacKind[]
  /** `parent` — зона над подзоной («Охотское море» над Западно-Камчатской). */
  rows: Array<{ vid: string; zone: string; parent: string | null; tons: Partial<Record<QuotaTacKind, number>> }>
}
export interface QuotaHolder {
  inn: string | null
  name: string
  tons: number
  share_of_zone: number | null
  kinds: Partial<Record<QuotaKind, number>>
  species: number[]
  contracts: number
}
export interface QuotasResponse {
  basins: string[]
  basin: string
  years: number[]
  year: number | null
  species: Array<{ code: number; name: string; tons: number }>
  zones: QuotaZone[]
  holders: QuotaHolder[]
  /** `gaps` — правки базы, которых нет в цепочке редакции (`chain_gaps.py`). */
  orders: Array<{ no: string; date: string; edition: number; gaps?: QuotaChainGap[] }>
  /** Лист карты промрайонов → районы квоты, которые его красят. */
  districts: Record<string, number[]>
  total_tons: number
}

/** Норма каталога в разделе «Правила» (`/cabinet/rules`). */
export interface RuleNorm {
  code: string
  point: string
  section: string
  kind: string
  title: string
  quote: string
  /** Оригинал, когда `quote` — перевод (юридическую силу имеет он); иначе `null`. */
  quote_original?: string | null
  /** Язык `quote`. */
  quote_lang?: string
  fishing_types: string[]
  when: Record<string, unknown>
  exceptions: Array<{ text: string; in_geometry: boolean }>
  readings: Array<{ text: string; why: string; chosen: boolean }>
  value: import('./rules/terms').RuleValue | null
  /** Коды аудиторий (`src/rules/vocab.py`): подпись — `rules.audience.<код>`. */
  audiences: string[]
  /** Документ нормы — ключ `RulesResponse.instruments`. */
  instrument: string
  place_code: string | null
  place_text: string | null
  districts: string[]
  drawn: boolean
  note: string | null
  verdict: import('./map/bans').BanVerdict
  /** «Все правила здесь»: here — место накрывает окрестность клика, edge —
   *  задето краем или межой районов, everywhere — норма без места, unknown —
   *  место без координат; null — точка не задана. */
  place: 'here' | 'edge' | 'everywhere' | 'unknown' | null
  /** Разрешение (разрешённое место или снятая исключением норма) — меткой. */
  permits: boolean
  /** Группа списка: applies | depends | conflict | excepted | idle. */
  group: string
  /** Противоречия, видимые в этом ответе: `with` — вторая норма пары. */
  conflicts: Array<{ kind: string; with: string | null; detail: string }>
}

/** Что известно о месте под кликом (`src/rules/point_facts.py`). Интервалы —
 *  свойство в окрестности клика; `null` — не знаем. */
export interface RulesPoint {
  lat: number
  lon: number
  radius_m: number
  district: { status: string; code?: string; name?: string; zone?: string | null } | null
  districts: string[]
  places_here: string[]
  places_edge: string[]
  depth: [number, number] | null
  from_shore_nm: [number, number] | null
  land: boolean | null
  /** Бассейн каталога под точкой; `null` — вне всех бассейнов каталога. */
  basin: string | null
  thresholds: { depth: number[]; from_shore_nm: number[] }
}

export interface RulesResponse {
  region: string
  date: string
  norms: RuleNorm[]
  total: number
  facets: {
    sections: string[]; kinds: string[]; fishing_types: string[]; species: string[]; gears: string[]
    audiences?: string[]
    species_terms?: Array<{ key: string; asfis: string | null; latin: string | null; en: string | null }>
    gear_terms?: Array<{ key: string; isscfg: string | null; en: string | null }>
  }
  decree: string | null
  sections_done: string[] | null
  /** Каталоги для переключателя (`src/rules/__init__.catalogs_info`). */
  catalogs?: import('./rules/catalogSwitch').RulesCatalog[]
  point: RulesPoint | null
  /** Отвечает ли каталог за воду под точкой; `null` — точка не задана. */
  coverage: import('./rules/coverage').RulesCoverage | null
  /** Язык цитат каталога — язык самого документа. */
  quote_lang: string
  /** Документы норм каталога (`src/rules/instruments.py`). */
  instruments?: Record<string, RulesInstrument>
}

export interface RulesInstrument {
  name: string
  name_orig: string
  /** Язык оригинала. */
  lang: string
  url: string
  edition: string | null
  jurisdiction: string
}

export interface RulesQuery {
  /** `null` — каталог выбирает сервер: первый район домена с правилами. */
  region: string | null
  date: string | null
  section: string | null
  kind: string | null
  fishingType: string | null
  species: string | null
  gear: string | null
  vesselLength: number | null
  onlyActive: boolean
  q: string
  at: { lat: number; lon: number } | null
  radius: number | null
  /** Язык подписей ответа (названия документов). */
  lang?: string
}

export interface BanCollection {
  type: 'FeatureCollection'
  features: Array<GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon, BanProps>>
  meta?: {
    attribution: string
    disclaimer_key: string
    date: string
    /** Запреты, у которых геометрии нет: место названо без координат. */
    without_geometry: Array<{ code: string; title: string; reason: string | null }>
  }
}

export interface EezCollection {
  type: 'FeatureCollection'
  features: Array<
    GeoJSON.Feature<GeoJSON.MultiLineString | GeoJSON.LineString, EezLineProperties>
  >
  meta?: {
    zoom: number
    tolerance_deg: number
    attribution: string
    disclaimer_key: string
  }
}

/** Сырая сетка SST на вьюпорт для браузерных изотерм. Значения по строкам
 *  (строка 0 — север), null = нет данных; vmin/vmax — под диапазон ползунка. */
export interface SstGridResponse {
  lat_top: number | null
  lon_left: number | null
  /** Шаг решётки, градусы. Равен широтному; читать им обе оси вправе только
   *  квадратная сетка (geo9). Оставлен ради совместимости. */
  step: number | null
  /** Шаг по широте и по долготе порознь: решётка региона бывает не квадратной
   *  (балтийская миля — 1/60° и 1/36°). Может отсутствовать у старого ответа. */
  step_lat?: number | null
  step_lon?: number | null
  rows: number
  cols: number
  values: (number | null)[][]
  vmin: number | null
  vmax: number | null
}

/** Сетка волнения = SstGridResponse + источник (реальный VHM0 или оценка по ветру). */
export interface WaveGridResponse extends SstGridResponse {
  meta?: { wave_source?: 'cmems' | 'wind' }
}

/** Отсчёт в точке карты: вся среда ближайшего узла на дату и глубину.
 *
 *  `node: null` — узла сетки под точкой нет (суша, край маски): не ошибка,
 *  координаты мы всё равно знаем. Строки едут КОДАМИ (`id`, `unit`), слова
 *  подставляет `pointCardView`. Форма описана в `src/cabinet/point_probe.py`. */
export interface PointProbeResponse {
  lat: number
  lon: number
  date: string
  depth: number
  node: { id: number; lat: number; lon: number; distance_km: number } | null
  /** В чьей зоне точка. `high_seas` — настоящий ответ (открытое море),
   *  `unknown` — рубежи не загружены; путать их нельзя. Поле необязательно:
   *  витрина и бэкенд выкатываются порознь. */
  zone?: {
    status: 'eez' | 'high_seas' | 'unknown'
    pol_type?: string | null
    sovereign?: string | null
    geoname?: string | null
    iso?: string | null
  } | null
  /** В каком ПРОМЫСЛОВОМ районе точка — другой вопрос и другой ответ: рубеж
   *  ИЭЗ говорит, в чьей воде судно, район — по каким правилам там ловят.
   *  `outside` — настоящий ответ (открытая часть морей), `unknown` — сюда не
   *  смотрели; путать их нельзя. */
  district?: {
    status: 'district' | 'outside' | 'unknown'
    code?: string
    map_code?: string
    name?: string
    zone?: string | null
    kind?: string
    osm_code?: number | null
    note?: string | null
  } | null
  values: {
    id: string
    value: number | null
    unit: string
    depth: number | null
    direction?: number | null
  }[]
}

/** Численные края цветовой шкалы слоя для легенды — те же, что красят тайлы.
 *  null у обоих = шкала неизвестна (тайлов ещё нет / нечисловой слой). */
export interface LegendBounds {
  layer: string
  min: number | null
  max: number | null
}

/** «Траектории по HSI» («живая вода»): усики обратной лагранжевой адвекции
 *  ненулевых HSI-ячеек. Каждая фича — LineString origin→now→горизонт (первая
 *  координата — origin-кружок 7 дн назад, узел meta.now_index — «сейчас», далее
 *  прогнозное плечо длиной meta.forecast_days), окрашенная HSI в точке «сейчас».
 *  meta.anchor/origin — якоря дат для нарезки длины усика по дню. */
export interface HsiDriftProps {
  hsi: number
  beached: boolean
}
/** Почему усиков нет. Различать причины обязательно: расчёт HSI по кнопке
 *  помогает только при 'no_hsi'/'stale_hsi', а 'empty_field'/'no_currents' им не
 *  чинятся (см. models/hsiRecompute). */
export type HsiDriftReason =
  | 'ok'
  | 'no_hsi'
  | 'stale_hsi'
  | 'empty_field'
  | 'no_currents'
  /** Поле вида цело, но в семидневной истории течений дыры: усик не с чего
   *  вести назад. Чинится докачкой (бэкенд ставит её сам), не расчётом HSI. */
  | 'no_currents_history'
export interface HsiDriftCollection {
  type: 'FeatureCollection'
  features: Array<GeoJSON.Feature<GeoJSON.LineString, HsiDriftProps>>
  meta: {
    vmax: number
    advect_days: number
    depth: number
    currents_through: string
    anchor: string
    origin: string | null
    count: number
    /** Почему усиков нет: 'ok' | 'no_hsi' | 'stale_hsi' | 'empty_field' | 'no_currents'.
     *  По нему кнопка «Обновить» решает, запускать ли расчёт HSI (см. hsiRecompute). */
    reason: HsiDriftReason
    /** Сутки истории течений, которых в кадре нет (ISO). Пусто — история цела. */
    missing_history_days: string[]
    /** Поставленные задания докачки этих суток: по ним фронт ведёт полоску.
     *  Приходит только когда докачка реально поставлена в этом ответе. */
    history_jobs?: Array<{ task_id: string; date: string }>
    /** Шаг поля течений под усиком, часы: 6 (мгновенные срезы) или 24 (суточные
     *  средние). Отличие не косметическое: средняя за сутки скорость
     *  систематически слабее мгновенной, и час на таймере ролика честен только
     *  при шаге 6 — на суточном поле он был бы нарисованной точностью. */
    currents_step_h: number
    /** Сколько суток прогноза добавлено в хвост усика (0 — только история). */
    forecast_days: number
    /** Индекс узла «сейчас» в координатах усика (null у легаси-ответа).
     *  Нарезка по дню-ползунку режет до него, а не до конца усика. */
    now_index: number | null
    /** Дата конца усика: anchor + forecast_days. */
    horizon: string
  }
}

// === Реестр AIS-спутников (раздел «Спутники») ===


/** Раздел «Буи»: платформа in-situ на карте.
 *  `freshness` приезжает С СЕРВЕРА: пороги возраста зависят от типа платформы и
 *  описаны один раз в `src/cabinet/buoy_freshness.py`. */
export interface BuoyPlatformProps {
  platform_id: string
  platform_type: string | null
  name: string | null
  institution: string | null
  wmo_code: string | null
  source_text: string | null
  data_mode: string | null
  last_obs_utc: string | null
  age_h: number | null
  freshness: 'fresh' | 'stale' | 'unknown' | 'retired'
  sources: string[]
  params: string[]
  /** Невязка «измерение − модель». Свойства НЕТ вовсе, если сравнивать не с
   *  чем: ноль на его месте читался бы как «модель точна». */
  resid?: number
  resid_layer?: string
}

export interface BuoyPlatformsResponse {
  type: 'FeatureCollection'
  features: Array<{
    type: 'Feature'
    geometry: { type: 'Point'; coordinates: [number, number] }
    properties: BuoyPlatformProps
  }>
  meta: {
    shown: number
    retired: number
    types: Array<{ value: string; count: number }>
  }
}

/** Одно значение карточки — вместе с тем, ОТКУДА оно. */
export interface BuoyValue {
  param: string
  value: number
  unit: string | null
  depth_m: number | null
  time: string | null
  source: string
  qc: number | null
  adjusted: boolean
  /** Каналы разошлись в ОДИН срок — не дубль, а факт о данных. */
  disagrees: boolean
}

/** Невязка «модель − измерение» по одному параметру. */
export interface BuoyResidual {
  param: string
  layer: string
  measured: number
  model: number
  diff: number
  source: string
  time: string | null
}

export interface BuoyCard {
  platform: BuoyPlatformProps
  values: BuoyValue[]
  /** Пусто — сравнивать было не с чем (глубина, единица или пустое поле). */
  residuals: BuoyResidual[]
  series: Record<string, Array<{ time: string | null; value: number; depth_m: number | null }>>
  track: Array<{ time: string | null; lat: number; lon: number }>
  /** Кабинету закрыт «Показ гидрологии» — значения среды вычтены. */
  hydrology_hidden: boolean
}

/** Запись реестра — одна строка каталога спутников (ref_satellite + курация). */
export interface SatelliteRegistryItem {
  norad_id: number
  name: string
  category: string
  constellation: string | null
  company_key: string | null
  operator: string | null
  legal_entity: string | null
  country: string | null
  object_type?: string | null
  discovery_source?: string | null
  orbit_class: string | null
  inclination_deg: number | null
  period_min: number | null
  apogee_km: number | null
  perigee_km: number | null
  launch_date: string | null
  decay_date: string | null
  ops_status: string | null
  status: string | null
  tle_epoch: string | null
  is_ais_receiver: boolean
}

/** Значение фасета + число спутников с этим значением. */
export interface SatelliteFacetValue {
  value: string
  count: number
}

export interface SatelliteFacets {
  category: SatelliteFacetValue[]
  company: SatelliteFacetValue[]
  orbit_class: SatelliteFacetValue[]
  legal_entity: SatelliteFacetValue[]
  status: SatelliteFacetValue[]
  source: SatelliteFacetValue[]
}

/** Поток AIS-позиций одного созвездия за окно (витрина mv_ais_feed_stat).
 *  Второй признак жизни рядом с каталожным статусом: тот отражает только факт
 *  орбиты, а отказ приёмника — нет. Считается по созвездию, не по аппарату. */
export interface SatelliteFeed {
  providers: string[]
  last_utc: string | null
  age_hours: number | null
  positions_7d: number
  vessels_7d: number
  computed_at: string | null
}

export interface SatelliteFeeds {
  /** Ключ = company_key реестра (тот же, что в фасете «Компания»). */
  by_company: Record<string, SatelliteFeed>
  /** Поток, который к созвездию не привязать (GFW-агрегатор), или null. */
  unattributed: SatelliteFeed | null
}

export interface SatelliteRegistry {
  count: number
  generated_at: string
  satellites: SatelliteRegistryItem[]
  facets: SatelliteFacets
  feeds: SatelliteFeeds
}

/** Мгновенная позиция спутника на момент time (пропагация TLE на бэкенде). */
export interface SatellitePosition {
  norad_id: number
  name: string
  category: string
  operator: string | null
  orbit_class: string | null
  status: string | null
  lat: number
  lon: number
  altitude_km: number
  footprint_radius_km: number
  dead_zone_radius_km: number
}

export interface SatellitePositions {
  time: string
  count: number
  satellites: SatellitePosition[]
}

/** Одно окно пролёта спутника над районом: интервал видимости (start…end),
 *  длительность в минутах и минимальная дистанция подспутниковой точки до центра
 *  района за пролёт. */
export interface SatellitePass {
  start: string
  end: string
  duration_min: number
  min_distance_km: number
}

/** Ответ /passes: окна пролёта над районом (по умолчанию — Дальневосточный
 *  промысловый box). target — центр района, к которому меряется min_distance_km. */
export interface SatellitePasses {
  norad_id: string
  name: string
  target: { lat: number; lon: number }
  from: string
  /** Конец окна; у сходящего аппарата усечён моментом входа в атмосферу. */
  to: string
  /** Момент входа в атмосферу, на котором окно оборвано; null — аппарат на орбите. */
  decayed_at: string | null
  bbox: number[]
  count: number
  passes: SatellitePass[]
}

/** Карточка + орбита + один виток трека для конкретного спутника. */
export interface SatelliteOrbit {
  card: Record<string, unknown>
  orbit: {
    inclination_deg: number | null
    period_min: number | null
    apogee_km: number | null
    perigee_km: number | null
    orbit_class: string | null
  }
  track: {
    period_min: number | null
    path: { lat: number; lon: number }[]
    current: {
      lat: number
      lon: number
      altitude_km: number
      footprint_radius_km: number
      dead_zone_radius_km: number
    }
  }
}

// === cabinetApi — методы наполняются в фазах A–E ===


/** Ответ раздела «Снимки». `looked` — рамки осмотра, и они не украшение:
 *  пустой `marks` при пустом `looked` значит «сюда не смотрели», а при
 *  непустом — «искали, не нашли». */
/** Календарь датасета «Скаута»: дни месяца со снимками района (сутки UTC). */
export interface DatasetCalendarResponse {
  region: string
  month: string
  days: CalendarDay[]
  months: string[]
}

export interface DatasetDayResponse {
  region: string
  date: string
  scenes: DatasetScene[]
  records: DatasetRecord[]
  /** Отметки-суда без пары АИС. */
  marks?: DatasetMark[]
  /** Отметки без АИС уже записываются; `false` — таблицы ещё нет. */
  marks_ready?: boolean
  ais: AisFix[]
}

export interface DatasetAttempt {
  attempt_id: number
  algo_version: string
  run_kind: string
  started_utc: string | null
  finished_utc: string | null
  outcome: string
  reason: string | null
  nearest_mark_m: number | null
  verdict: string | null
  course_delta_deg: number | null
  evidence: Record<string, unknown>
}

export interface DatasetRecordResponse {
  record: DatasetRecord & {
    provider: string
    dt_before_s: number | null
    dt_after_s: number | null
    below_resolution: boolean
    daylight: boolean
    sensor: string
    captured_at: string
    algo_version_last: string | null
  }
  attempts: DatasetAttempt[]
}

export interface ImageryMarksResponse {
  marks: { type: 'FeatureCollection'; features: Array<{
    type: 'Feature'
    geometry: { type: 'Point'; coordinates: [number, number] }
    properties: Record<string, unknown>
  }> }
  looked: { type: 'FeatureCollection'; features: Array<{
    type: 'Feature'
    properties: Record<string, unknown>
  }> }
  /** Подложки осмотров, у которых артефакт лежит на диске; от свежей к старой.
   *  Едут ТЕМ ЖЕ ответом, что отметки: раздельные запросы дают кадр, где точки
   *  уже пришли, а картинок ещё нет, и эти полсекунды карта врёт. */
  pictures: Array<{
    sweep_id: number
    sensor: string
    source: string
    scene_id: string
    captured_at: string
    capture_date: string
    band: string
    scale: string
    unit: string
    lo: number
    hi: number
    transform: string
    bbox: { lat_min: number; lat_max: number; lon_min: number; lon_max: number }
  }>
  searched_km2: number
  /** Пороги прибора: карточка объясняет ими вердикт. Живут на СЕРВЕРЕ —
   *  вторая копия во фронте разъехалась бы с прибором молча. */
  thresholds?: {
    wake_strength: [number, number]
    wake_width_m: [number, number]
    wake_rings: number
    area_px: number
    background_ratio: number
  }
  facets: { verdict: Record<string, number>; sensor: Record<string, number> }
  ais: {
    asked: boolean
    matched: number
    attribution: string
    show_mmsi: boolean
    /** Источники, чьё сведение есть в базе, но показывать его нельзя. */
    withheld: string[]
  }
}

/** Прогон кнопки «Загрузить» в «Скауте»: осмотр рамки за окно дат. */
export interface ImageryScanJob {
  job_id: number
  status: 'queued' | 'running' | 'done' | 'failed'
  phase: string | null
  percent: number
  /** Сколько сцен ЕСТЬ над рамкой и сколько взято в прогон: потолок называется
   *  числом, иначе неполный осмотр выглядит как полный. */
  scenes_found: number
  scenes_total: number
  scenes_done: number
  marks: number
  error: string | null
  /** Осмотр одного снимка календаря: `источник:номер`; `null` — осмотр рамки. */
  scene_key?: string | null
}

export interface PassesCalendarResponse {
  region: string
  month: string
  days: Array<{ date: string; optical: number; radar: number }>
  months: string[]
}

export interface PassesDayResponse {
  region: string
  date: string
  scenes: DatasetScene[]
}

export const cabinetApi = {
  orgs: () => getJson<{ orgs: OrgSummary[]; active_org_id: number | null }>('/api/v1/cabinet/orgs'),
  switchOrg: (orgId: number) =>
    postJson<{ access_token: string; active_org_id: number }>('/api/v1/cabinet/orgs/switch', { org_id: orgId }),

  // Данные по кнопке «Загрузить»: prepare (план) → confirm (докачка) → progress (поллинг)
  prepare: (body: PrepareBody) =>
    postJson<PrepareResponse>('/api/v1/cabinet/data/prepare', body),
  // Статус фонового обновления судов, запущенного тем же prepare.
  fleetRefreshStatus: (jobId: string) =>
    getJson<FleetRefreshResponse>(`/api/v1/cabinet/fleet/refresh/${jobId}`),
  // Прогресс точечного добора позиций по карточкам — второй, медленный слой.
  cardJobStatus: (jobId: number) =>
    getJson<CardJobStatus>(`/api/v1/cabinet/fleet/cards/${jobId}`),
  // Спросить позицию ОДНОГО судна — кнопка в его карточке. Прогресс читается
  // тем же cardJobStatus: путь работы общий, разный только адрес задания.
  refreshVessel: (mmsi: string) =>
    postJson<{ job_id: number | null; joined?: boolean }>(
      `/api/v1/cabinet/fleet/vessel/${encodeURIComponent(mmsi)}/refresh`,
      {},
    ),
  // Что уже нарисовано по виду и дате. Нужен автопоказу поля вероятности: у
  // модельных слоёв availability отвечает «не готово» по определению (они ждут
  // «Загрузить»), а спросить «есть ли чем красить» больше негде.
  rasterStatus: (speciesId: number, date: string) =>
    getJson<RasterStatus>(`/api/v1/tiles/raster/status/${speciesId}/${date}`),
  confirm: (taskId: string) =>
    postJson<{ task_id: string; status: string }>(`/api/v1/cabinet/data/${taskId}/confirm`),
  progress: (taskId: string) =>
    getJson<ProgressResponse>(`/api/v1/cabinet/data/progress/${taskId}`),

  // Лёгкая read-only проверка готовности слоя (не создаёт задачу)
  availability: (params: {
    layer: string; date: string; depth: number; bbox: BBox
    /** Вид — обязателен слою `nn`: поле считается ПО ВИДУ, и без него сервер
     *  отвечает 400, а не «данных нет» (забытый параметр не должен выглядеть
     *  как «не считали»). Прочим слоям безразличен. */
    species_id?: number | null
  }) =>
    getJson<Availability>(
      '/api/v1/cabinet/data/availability' +
        buildQuery({
          layer: params.layer,
          date: params.date,
          depth: params.depth,
          species_id: params.species_id ?? undefined,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
        }),
    ),

  // Стрелки направления течений: прореженные векторы u/v по bbox.
  // Шаг прореживания считает фронт (arrows.ts) — он зависит от зума.
  currentsArrows: (params: {
    date: string
    depth: number
    stepDeg: number
    bbox: BBox
    /** Оверлей «Волнение»: добавить wave_hs (оценка по ветру) в узлы стрелок. */
    waves?: boolean
  }) =>
    getJson<ArrowsCollection>(
      '/api/v1/cabinet/currents/arrows' +
        buildQuery({
          date: params.date,
          depth: params.depth,
          step_deg: params.stepDeg,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
          waves: params.waves ? true : undefined,
        }),
    ),

  // Придонные стрелки апвеллинга: из артефакта расчёта за дату (не из БД).
  // Именно придонные — отрыв планктона создаёт течение у дна, поверхностные
  // стрелки поверх этого поля показывали бы чужое направление.
  upwellingArrows: (params: { date: string; stepDeg: number; bbox: BBox }) =>
    getJson<ArrowsCollection>(
      '/api/v1/cabinet/upwelling/arrows' +
        buildQuery({
          date: params.date,
          step_deg: params.stepDeg,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
        }),
    ),

  // Рубежи экономических зон вьюпорта. Даты нет: границы меняются раз в годы.
  // `zoom` управляет упрощением на бэкенде — один 200-мильный предел России это
  // 6,5 тыс. км линии, и без упрощения ответ был бы мегабайтным.
  eez: (params: { bbox: BBox; zoom: number }) =>
    getJson<EezCollection>(
      '/api/v1/cabinet/eez' +
        buildQuery({
          zoom: params.zoom,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
        }),
    ),

  // Раздел «Квоты»: год, вид, район. Рамки нет — квота выдаётся на район целиком.
  pricesSummary: () => getJson<PricesSummary>('/api/v1/cabinet/prices/summary'),
  aisGapReports: () => getJson<AisGapJobs>('/api/v1/cabinet/reports/ais-gaps'),
  aisGapReport: (id: number) => getJson<AisGapReport>(`/api/v1/cabinet/reports/ais-gaps/${id}`),
  deleteAisGapReport: (id: number) =>
    deleteJson<{ deleted: number }>(`/api/v1/cabinet/reports/ais-gaps/${id}`),
  createAisGapReport: (body: AisGapCreate) =>
    postJson<{ group: number; accepted: number; rejected: string[];
               sources: ({ source: 1 | 2; id: number; t0: string; t1: string }
                         | { source: 1 | 2; skipped: 'no_data' | 'unavailable' })[] }>(
      '/api/v1/cabinet/reports/ais-gaps', body),
  customsSpecies: (market: 'export' | 'wholesale' | 'wholesale_ru' = 'export') =>
    getJson<{ species: CustomsSpecies[] }>(`/api/v1/cabinet/prices/customs?market=${market}`),
  customsSeries: (species: string, market: 'export' | 'wholesale' | 'wholesale_ru' = 'export') =>
    getJson<CustomsSeries>(
      `/api/v1/cabinet/prices/customs?market=${market}&species=${encodeURIComponent(species)}`),
  pricesAnalyticsSpecies: () =>
    getJson<{ species: { code: string; name_ru: string; name_en: string }[] }>(
      '/api/v1/cabinet/prices/analytics'),
  pricesAnalytics: (species: string) =>
    getJson<PricesOverview>(`/api/v1/cabinet/prices/analytics?species=${encodeURIComponent(species)}`),
  pricesBasket: () =>
    getJson<{ index: { month: string; index: number; items: number }[] }>(
      '/api/v1/cabinet/prices/basket'),
  retailTerritories: () =>
    getJson<{ territories: string[] }>('/api/v1/cabinet/prices/retail'),
  retailSeries: (city: string) =>
    getJson<RetailSeries>(`/api/v1/cabinet/prices/retail?city=${encodeURIComponent(city)}`),
  retailChainDays: () =>
    getJson<{ days: { city: string; day: string; points: number }[] }>(
      '/api/v1/cabinet/prices/retail/chains'),
  retailChains: (city: string, day: string) =>
    getJson<ChainsSeries>(`/api/v1/cabinet/prices/retail/chains?city=${encodeURIComponent(city)}`
      + `&day=${encodeURIComponent(day)}`),
  quotas: (params: { basin: string; year: number | null; species: number | null; zone: number | null }) =>
    getJson<QuotasResponse>(
      '/api/v1/cabinet/quotas' +
        buildQuery({ basin: params.basin, year: params.year ?? undefined,
          vbr_code: params.species ?? undefined, zone_code: params.zone ?? undefined }),
    ),
  quotaShares: (basin: string) =>
    getJson<QuotaSharesResponse>('/api/v1/cabinet/quotas/shares' + buildQuery({ basin })),
  quotaTac: (params: { basin: string; year: number | null }) =>
    getJson<QuotaTacResponse>('/api/v1/cabinet/quotas/tac'
      + buildQuery({ basin: params.basin, year: params.year ?? undefined })),

  // Промысловые зоны и подзоны вьюпорта. Даты нет: границы меняются приказом
  // раз в годы. `zoom` управляет упрощением на бэкенде — весь Дальний Восток
  // без него это сотни килобайт на каждый пан.
  fishingDistricts: (params: { bbox: BBox; zoom: number }) =>
    getJson<DistrictCollection>(
      '/api/v1/cabinet/fishing-districts' +
        buildQuery({
          zoom: params.zoom,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
        }),
    ),

  // Навигационные предупреждения NGA в силе на дату внутри вьюпорта.
  navwarn: (params: { bbox: BBox; date: string | null }) =>
    getJson<NavwarnCollection>(
      '/api/v1/cabinet/navwarn' +
        buildQuery({
          date: params.date ?? undefined,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
        }),
    ),

  navwarnMessage: (navarea: string, year: number, number: number) =>
    getJson<NavwarnFull>(`/api/v1/cabinet/navwarn/${navarea}/${year}/${number}`),

  // Тип дна по картам ГГК-1000 внутри вьюпорта.
  substrate: (params: { bbox: BBox; zoom: number }) =>
    getJson<SubstrateCollection>(
      '/api/v1/cabinet/substrate' +
        buildQuery({
          zoom: params.zoom,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
        }),
    ),

  // Запретные районы вьюпорта. Дата и вид — не отбор, а ответ: у каждого
  // запрета сервер считает `active` (в силе на дату) и `applies` (для вида).
  fishingBans: (params: { bbox: BBox; zoom: number; date: string | null; species: number | null }) =>
    getJson<BanCollection>(
      '/api/v1/cabinet/fishing-bans' +
        buildQuery({
          zoom: params.zoom,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
          date: params.date ?? undefined,
          species: params.species ?? undefined,
          // Тексты запретов — на языке кабинета (память переводов на сервере).
          lang: getLang(),
        }),
    ),

  // Раздел «Правила»: нормы каталога со статусом вычислителя.
  rules: (q: RulesQuery) =>
    getJson<RulesResponse>(
      '/api/v1/cabinet/rules' +
        buildQuery({
          region: q.region ?? undefined,
          date: q.date ?? undefined,
          section: q.section ?? undefined,
          kind: q.kind ?? undefined,
          fishing_type: q.fishingType ?? undefined,
          species: q.species ?? undefined,
          gear: q.gear ?? undefined,
          vessel_length: q.vesselLength ?? undefined,
          only_active: q.onlyActive ? 'true' : undefined,
          q: q.q.trim() || undefined,
          lat: q.at?.lat,
          lon: q.at?.lon,
          r: q.at ? q.radius ?? undefined : undefined,
          lang: q.lang,
        }),
    ),

  // Места отобранных норм во вьюпорте — тот же слой запретов, ограниченный кодами.
  rulesGeo: (params: { bbox: BBox; zoom: number; codes: string[]; date: string | null }) =>
    getJson<BanCollection>(
      '/api/v1/cabinet/rules/geo' +
        buildQuery({
          zoom: params.zoom,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
          codes: params.codes.join(','),
          date: params.date ?? undefined,
          lang: getLang(),
        }),
    ),

  // Изобаты вьюпорта. Даты нет: рельеф дна статичен. zoom управляет упрощением
  // на бэкенде — без него ответ на весь Дальний Восток был бы мегабайтным.
  isobaths: (params: { bbox: BBox; zoom: number; step?: number }) =>
    getJson<IsobathCollection>(
      '/api/v1/cabinet/isobaths' +
        buildQuery({
          zoom: params.zoom,
          // Без шага — пятёрка апвеллинга (его карта не меняется).
          step: params.step,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
        }),
    ),

  // Сырая сетка SST на вьюпорт — источник браузерных изотерм. Один запрос на
  // (дата, глубина, вьюпорт); ползунок градуса потом считает контур по кэшу.
  // Сетка высоты волны Hs (м): реальный VHM0 (CMEMS) где есть, иначе оценка по
  // ветру (6a). meta.wave_source — 'cmems'|'wind'. Форма — как sstGrid, чтобы
  // рисовать изо-линии тем же движком marchingSquares.
  wavesGrid: (params: { date: string; bbox: BBox }) =>
    getJson<WaveGridResponse>(
      '/api/v1/cabinet/waves/grid' +
        buildQuery({
          date: params.date,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
        }),
    ),

  /** Выгрузка кадра одним файлом NetCDF: все узлы, горизонты и параметры. */
  exportGrid: (params: { date: string; bbox: BBox; species?: number | null }) =>
    getBytes(
      '/api/v1/cabinet/export/grid.nc' +
        buildQuery({
          date: params.date,
          species: params.species ?? undefined,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
        }),
    ),

  sstGrid: (params: { date: string; depth: number; bbox: BBox }) =>
    getJson<SstGridResponse>(
      '/api/v1/cabinet/sst/grid' +
        buildQuery({
          date: params.date,
          depth: params.depth,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
        }),
    ),

  /** Отсчёт в точке карты. `species` не указан — модельных строк в ответе не
   *  будет: пригодность не существует без вида, а пустая строка на «Картах»
   *  читалась бы как поломка. */
  point: (params: {
    lat: number
    lon: number
    date: string
    depth: number
    species?: number | null
  }) =>
    getJson<PointProbeResponse>(
      '/api/v1/cabinet/point' +
        buildQuery({
          lat: params.lat,
          lon: params.lon,
          date: params.date,
          depth: params.depth,
          species: params.species ?? undefined,
          // Район и запреты под точкой — тексты приказа, на языке кабинета.
          lang: getLang(),
        }),
    ),

  // Края цветовой шкалы слоя (для чисел на легенде). Дёшево, read-only; ключуем
  // на фронте по (слой, дата, глубина, версия тайлов), чтобы совпадало с картинкой.
  // `region` — район под центром кадра (`regionAt`): шкала у каждого района своя.
  legend: (params: { layer: string; date: string; depth: number; region?: string | null }) =>
    getJson<LegendBounds>(
      '/api/v1/cabinet/legend' +
        buildQuery({
          layer: params.layer, date: params.date, depth: params.depth,
          region: params.region ?? undefined,
        }),
    ),

  /** Каталог слоёв по правам кабинета. `hydrology=false` — среда закрыта
   *  признаком организации: её слоёв в списке нет, а инструменты среды прячет
   *  витрина (см. `src/hydrology.ts`). */
  layers: () => getJson<LayerCatalog>('/api/v1/cabinet/layers'),

  /** Двадцать мест нейрокарты и провенанс — ОДНИМ ответом.
   *
   *  Раздельные запросы дали бы кадр, где точки уже пришли, а числа, которыми
   *  их читают, ещё нет: маска «где ловили вчера» могла накрыть почти всю воду,
   *  и тогда двадцатка выбрана из остатка. */
  nnMarks: (species: number, date: string) =>
    getJson<NnMarks>(
      '/api/v1/cabinet/nn/marks' + buildQuery({ species, date }),
    ),

  /** Готовность ВСЕЙ семьи механизмов апвеллинга за дату — одним запросом.
   *  Панель показывает, какие слои сегодня есть, а какие пусты; шесть запросов
   *  на открытие раздела означали бы шесть чтений одного каталога сайдкаров. */
  upwellingMechanisms: (date: string) =>
    getJson<{ date: string; mechanisms: UpwMechanismState[] }>(
      `/api/v1/cabinet/upwelling/mechanisms?date=${encodeURIComponent(date)}`),

  /** Сетка значений механизма на вьюпорт — источник браузерных изолиний.
   *  Набор ключей совпадает с `/cabinet/sst/grid`: линии считает тот же
   *  marching squares. */
  upwellingGrid: (p: {
    mechanism: string; date: string; depth?: number | null; bbox: BBox
  }) => {
    const q = new URLSearchParams({
      mechanism: p.mechanism, date: p.date,
      lat_min: String(p.bbox.lat_min), lat_max: String(p.bbox.lat_max),
      lon_min: String(p.bbox.lon_min), lon_max: String(p.bbox.lon_max),
    })
    if (p.depth != null) q.set('depth', String(p.depth))
    return getJson<SstGridResponse>(`/api/v1/cabinet/upwelling/grid?${q}`)
  },

  /** Виды и регионы ОДНИМ ответом. Отдельная ручка регионов завела бы
   *  состояние «виды приехали, регионы ещё нет», в котором фильтру нечего
   *  показывать; кадр в запрос не уходит — иначе он попал бы в ключ кэша. */
  species: () =>
    getJson<{ species: SpeciesItem[]; regions?: RegionItem[] }>('/api/v1/cabinet/species'),

  /** Шпаргалка изотерм: диапазоны `sst` сезонной модели пелагиков на месяц
   *  даты. Числа и коды — слова берёт каталог (`maps.isotherm.cheat.*`). */
  thermalHints: (date: string) =>
    getJson<ThermalHintsResponse>(
      '/api/v1/cabinet/species/thermal-hints' + buildQuery({ date }),
    ),

  /** Подпись под легендой на скачанной карте; правится в админке, пустая
   *  строка — «не выводить ничего». */
  exportCaption: () => getJson<{ text: string }>('/api/v1/cabinet/export-caption'),

  earthquakes: (bbox: BBox, date: string) =>
    getJson<GeoJSONFeatureCollection>(
      '/api/v1/cabinet/earthquakes' +
        buildQuery({ ...bbox, date }),
    ),

  // bbox обязателен по смыслу: без него потолок выборки тратится на новейшие
  // операции по всему океану и в поле зрения может не остаться ни одной точки.
  fleetPositions: (params: {
    dateFrom: string
    dateTo: string
    scope: 'own' | 'all'
    speciesId?: number | null
    bbox: BBox
  }) =>
    getJson<FleetCollection>(
      '/api/v1/cabinet/fleet/positions' +
        buildQuery({
          date_from: params.dateFrom,
          date_to: params.dateTo,
          scope: params.scope,
          species_id: params.speciesId ?? undefined,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
        }),
    ),

  // Живые AIS-позиции судов для слоя «Флот» на «Моделях». Единый источник —
  // таблица ais_vessel (коллектор + enrichment). scope=own|ru|foreign — один
  // запрос на включённый сегмент; speciesId (вид из падающего списка) фильтрует
  // по типу промысла NPFC, None → все виды; includeUncategorized (галка «суда
  // без категорий») при выбранном виде пускает заодно суда без типа промысла
  // NPFC; bbox — видимая область карты. Возвращает GeoJSON FeatureCollection.
  fleetVessels: (params: {
    scope: 'own' | 'nat' | 'foreign'
    speciesId?: number | null
    includeUncategorized?: boolean
    bbox: BBox
    /** Флаги «Ин.флота»; пусто/undefined = все. */
    flags?: string[]
  }) =>
    getJson<VesselCollection>(
      '/api/v1/cabinet/fleet/vessels' +
        buildQuery({
          scope: params.scope,
          species_id: params.speciesId ?? undefined,
          include_uncategorized: params.includeUncategorized ? true : undefined,
          flags: params.flags?.length ? params.flags.join(',') : undefined,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
        }),
    ),

  // Положения тех же судов ЗА ОКНО — из этого в ролике едут суда. Отбор
  // повторяет fleetVessels (в кадре обязан ехать тот флот, что человек видит),
  // добавлены только края окна записи. Ответ не GeoJSON: у каждого судна СПИСОК
  // положений, нарезка по моменту кадра идёт локально (map/fleetHistory.ts).
  fleetVesselsHistory: (params: {
    scope: 'own' | 'nat' | 'foreign'
    /** Края окна записи, 'YYYY-MM-DD' включительно. */
    from: string
    to: string
    speciesId?: number | null
    includeUncategorized?: boolean
    bbox: BBox
    flags?: string[]
  }) =>
    getJson<FleetHistory>(
      '/api/v1/cabinet/fleet/vessels/history' +
        buildQuery({
          scope: params.scope,
          from: params.from,
          to: params.to,
          species_id: params.speciesId ?? undefined,
          include_uncategorized: params.includeUncategorized ? true : undefined,
          flags: params.flags?.length ? params.flags.join(',') : undefined,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
        }),
    ),

  // Подсказки поиска судна для строки в сайдбаре: имя или MMSI → суда с
  // координатами (выбор ведёт карту к судну). Имена сравниваются
  // нормализованными: часть их записана кириллическими омоглифами.
  vesselSearch: (q: string) =>
    getJson<{ vessels: VesselSearchHit[] }>(
      '/api/v1/cabinet/fleet/search' + buildQuery({ q }),
    ),

  // Трек судна за период: позиции АИС, покрашенные операциями (переход, поиск,
  // промысел, дрейф, стоянка) из предрасчёта `ais_track_segment`. Те же данные,
  // что в разделе «Флот». `meta.available_from/to` — период, за который разметка
  // у судна вообще есть: по нему фронт поправляет окно (см. periodFromMeta).
  vesselTrack: (mmsi: string, from: string, to: string) =>
    getJson<VesselTrackCollection>(
      `/api/v1/cabinet/fleet/vessel/${encodeURIComponent(mmsi)}/track` +
        buildQuery({ from, to }),
    ),

  // Накопительный снос: читает готовые треки из drift_trajectories (не считает).
  // `date` — просматриваемый день: сервер отдаёт наблюдения за семь суток до
  // него (окно одно на всех — ползунок «Свежесть» убран 11.09.2026). Без даты
  // якорем было бы сегодня, и перемотка в прошлое не возвращала бы старые
  // отметки (docs/reports/2026-08-08-catch-points-audit.md).
  drift: (speciesId: number, date?: string) =>
    getJson<DriftCollection>(
      '/api/v1/cabinet/currents/drift' +
        buildQuery({ species_id: speciesId, date }),
    ),

  // «Траектории по HSI»: обратный снос ненулевых HSI-ячеек вида. Один запрос на
  // (вид, дата, глубина, вьюпорт). Дата — день ПОЛЗУНКА: бэкенд берёт последний
  // прогноз ≤ неё как якорь, и усик всегда кончается там, длиной 7 суток. До
  // 11.09.2026 шла дата «сегодня», а ползунок резал усик по кэшу — длина на
  // экране зависела от выбранного дня.
  hsiDrift: (params: {
    species: number
    date: string
    depth: number
    strideDeg: number
    bbox: BBox
    /** Заодно докачать недостающие сутки истории течений. Ставит его ТОЛЬКО
     *  клик по «Загрузить/Обновить» (см. shouldFillHistory): этот запрос уходит
     *  на каждый сдвиг карты, и без флага докачка шла бы от панорамирования. */
    fillHistory?: boolean
  }) =>
    getJson<HsiDriftCollection>(
      '/api/v1/cabinet/hsi-drift' +
        buildQuery({
          species: params.species,
          date: params.date,
          depth: params.depth,
          stride_deg: params.strideDeg,
          lat_min: params.bbox.lat_min,
          lat_max: params.bbox.lat_max,
          lon_min: params.bbox.lon_min,
          lon_max: params.bbox.lon_max,
          fill_history: params.fillHistory ? 1 : undefined,
        }),
    ),

  clusters: () => getJson<{ clusters: Cluster[] }>('/api/v1/cabinet/clusters'),
  createCluster: (body: ClusterInput) => postJson<Cluster>('/api/v1/cabinet/clusters', body),
  updateCluster: (id: number, body: ClusterInput) =>
    patchJson<Cluster>(`/api/v1/cabinet/clusters/${id}`, body),
  deleteCluster: (id: number) => deleteJson<{ deleted: number }>(`/api/v1/cabinet/clusters/${id}`),

  savedViews: () => getJson<{ saved_views: SavedView[] }>('/api/v1/cabinet/saved-views'),
  createSavedView: (name: string, urlState: Record<string, unknown>) =>
    postJson<SavedView>('/api/v1/cabinet/saved-views', { name, url_state: urlState }),
  deleteSavedView: (id: number) => deleteJson<{ deleted: number }>(`/api/v1/cabinet/saved-views/${id}`),

  orgMembers: () => getJson<{ members: OrgMember[] }>('/api/v1/cabinet/org/members'),
  addMember: (email: string, role: string) =>
    postJson<OrgMember>('/api/v1/cabinet/org/members', { email, role }),
  setMemberRole: (userId: number, role: string) =>
    putJson<{ user_id: number; role: string }>(`/api/v1/cabinet/org/members/${userId}`, { role }),
  removeMember: (userId: number) =>
    deleteJson<{ removed: number }>(`/api/v1/cabinet/org/members/${userId}`),
  updateOrgSettings: (settings: Record<string, unknown>) =>
    putJson<{ ok: boolean }>('/api/v1/cabinet/org/settings', { settings }),

  // Раздел «Наблюдаемость» (вкладка «Спутников»): факт молчания судов —
  // сводка за период, карта ячеек, рейтинг судов, сравнение подписок и лента
  // провалов одного судна. Причины молчания API не утверждает (см. disclaimer).
  observability: {
    summary: (from: string, to: string): Promise<ObsSummary> =>
      getJson<ObsSummary>('/api/v1/cabinet/observability/summary' + buildQuery({ from, to })),
    map: (from: string, to: string): Promise<{ cells: ObsCell[]; disclaimer: string }> =>
      getJson<{ cells: ObsCell[]; disclaimer: string }>(
        '/api/v1/cabinet/observability/map' + buildQuery({ from, to }),
      ),
    vessels: (from: string, to: string, limit?: number): Promise<{ vessels: ObsVessel[] }> =>
      getJson<{ vessels: ObsVessel[] }>(
        '/api/v1/cabinet/observability/vessels' + buildQuery({ from, to, limit }),
      ),
    providers: (): Promise<{ scopes: Record<string, ObsScope> }> =>
      getJson<{ scopes: Record<string, ObsScope> }>('/api/v1/cabinet/observability/providers'),
    vessel: (
      mmsi: string,
      from: string,
      to: string,
    ): Promise<{ gaps: ObsGap[]; blind_hours: number; disclaimer: string }> =>
      getJson<{ gaps: ObsGap[]; blind_hours: number; disclaimer: string }>(
        '/api/v1/cabinet/observability/vessel/' +
          encodeURIComponent(mmsi) +
          buildQuery({ from, to }),
      ),
  },

  // Раздел «Буи»: платформы in-situ в кадре и карточка одной платформы со всеми
  // известными значениями и источником у каждого.
  // Раздел «Снимки»: отметки судов на радаре и оптике вместе с рамками ОСМОТРА.
  // Ответ ОДИН: раздельные запросы дают кадр, где точки уже пришли, а рамок
  // ещё нет, — и эти полсекунды пустая карта значит «сюда не смотрели».
  imagery: {
    /** `date` — сутки UTC снимка из календаря пролётов; без него — последние `days`. */
    marks: (
      bbox: { lat_min: number; lat_max: number; lon_min: number; lon_max: number },
      days: number,
      date?: string | null,
    ): Promise<ImageryMarksResponse> =>
      getJson<ImageryMarksResponse>(
        '/api/v1/cabinet/imagery/marks' +
          buildQuery({
            lat_min: bbox.lat_min,
            lat_max: bbox.lat_max,
            lon_min: bbox.lon_min,
            lon_max: bbox.lon_max,
            days,
            ...(date ? { date } : {}),
          }),
      ),
    /** Нажатие кнопки «Загрузить»: осмотреть рамку кадра за окно дат.
     *
     *  Ничего не спрашивает — то же решение, что у кнопки «Загрузить» на
     *  «Моделях». Повторное нажатие возвращает ИДУЩЕЕ задание, а не ошибку:
     *  отказ читался бы как сломанная кнопка. */
    scan: (
      bbox: { lat_min: number; lat_max: number; lon_min: number; lon_max: number },
      days: number,
      scene?: string | null,
    ): Promise<{ job: ImageryScanJob | null }> =>
      postJson<{ job: ImageryScanJob | null }>('/api/v1/cabinet/imagery/scan',
                                               { ...bbox, days, ...(scene ? { scene } : {}) }),
    /** Пролёты района — любому кабинету с разделом: календарь и снимки дня. */
    passes: {
      calendar: (region: string, month: string): Promise<PassesCalendarResponse> =>
        getJson<PassesCalendarResponse>(
          '/api/v1/cabinet/imagery/passes/calendar' + buildQuery({ region, month })),
      day: (region: string, date: string): Promise<PassesDayResponse> =>
        getJson<PassesDayResponse>(
          '/api/v1/cabinet/imagery/passes/day' + buildQuery({ region, date })),
    },
    /** Что сейчас делает кнопка. Спрашивается и после перезагрузки страницы:
     *  прогон живёт в базе, а не во вкладке. */
    scanStatus: (): Promise<{ job: ImageryScanJob | null }> =>
      getJson<{ job: ImageryScanJob | null }>('/api/v1/cabinet/imagery/scan'),
    /** Режим «Датасет»: только кабинету с признаком `imagery_dataset`, иначе 403. */
    dataset: {
      calendar: (region: string, month: string): Promise<DatasetCalendarResponse> =>
        getJson<DatasetCalendarResponse>(
          '/api/v1/cabinet/imagery/dataset/calendar' + buildQuery({ region, month })),
      day: (region: string, date: string): Promise<DatasetDayResponse> =>
        getJson<DatasetDayResponse>(
          '/api/v1/cabinet/imagery/dataset/day' + buildQuery({ region, date })),
      record: (recordId: number): Promise<DatasetRecordResponse> =>
        getJson<DatasetRecordResponse>(`/api/v1/cabinet/imagery/dataset/record/${recordId}`),
    },
  },

  buoys: {
    platforms: (
      bbox: { lat_min: number; lat_max: number; lon_min: number; lon_max: number },
      types?: string[],
      // Слой невязки. Пусто — обычный вид: раскраска по свежести не платит за
      // три запроса, которых не показывает.
      residual?: string | null,
    ): Promise<BuoyPlatformsResponse> =>
      getJson<BuoyPlatformsResponse>(
        '/api/v1/cabinet/buoys/platforms' +
          buildQuery({
            lat_min: bbox.lat_min,
            lat_max: bbox.lat_max,
            lon_min: bbox.lon_min,
            lon_max: bbox.lon_max,
            types: types && types.length ? types.join(',') : undefined,
            residual: residual || undefined,
          }),
      ),
    card: (platformId: string): Promise<BuoyCard> =>
      getJson<BuoyCard>(
        '/api/v1/cabinet/buoys/platform/' + encodeURIComponent(platformId),
      ),
  },

  // Раздел «Спутники»: реестр AIS-приёмных КА, мгновенные позиции (пропагация TLE
  // на дату/время) и орбита+трек одного спутника по NORAD id.
  satellites: {
    registry: (): Promise<SatelliteRegistry> =>
      getJson<SatelliteRegistry>('/api/v1/cabinet/satellites/registry'),
    // time опционален: без него бэкенд берёт «сейчас».
    positions: (time?: string): Promise<SatellitePositions> =>
      getJson<SatellitePositions>('/api/v1/cabinet/satellites/positions' + buildQuery({ time })),
    orbit: (norad: string, time?: string): Promise<SatelliteOrbit> =>
      getJson<SatelliteOrbit>(
        '/api/v1/cabinet/satellites/' + encodeURIComponent(norad) + '/orbit' + buildQuery({ time }),
      ),
    // Окна пролёта над районом. Все опции опциональны: бэкенд по умолчанию берёт
    // from=сейчас, to=+24ч, bbox=Дальневосточный промысловый box. Параметр бэкенда
    // именно `from` (не from_) — buildQuery ставит ключ буквально.
    passes: (
      norad: string,
      opts?: { from?: string; to?: string; bbox?: string },
    ): Promise<SatellitePasses> =>
      getJson<SatellitePasses>(
        '/api/v1/cabinet/satellites/' +
          encodeURIComponent(norad) +
          '/passes' +
          buildQuery({ from: opts?.from, to: opts?.to, bbox: opts?.bbox }),
      ),
  },
}

// --- Наблюдаемость: когда и где суда молчали -------------------------------
// Раздел ОПИСАТЕЛЬНЫЙ. Он сообщает измеренный факт (судно не давало позиций
// дольше собственной нормы) и НЕ объясняет причину: гипотеза о спутниковом
// покрытии проверена на 6.66 млн точек подтверждённого приёма и отвергнута
// (docs/plans/2026-08-02-ring-calibration-nogo.md). Поле disclaimer приходит
// вместе с числами и обязано показываться рядом с ними.

export interface ObsBucket {
  bucket: string
  count: number
}

export interface ObsHour {
  hour: number
  count: number
}

export interface ObsSummary {
  from: string
  to: string
  gaps: number
  vessels: number
  blind_hours: number
  median_min: number | null
  max_min: number | null
  low_confidence: number
  buckets: ObsBucket[]
  hour_profile: ObsHour[]
  disclaimer: string
}

export interface ObsCell {
  lat: number
  lon: number
  size_deg: number
  gaps: number
  vessels: number
  blind_hours: number
}

export interface ObsVessel {
  mmsi: string
  name: string
  gaps: number
  blind_hours: number
  max_min: number | null
}

export interface ObsScope {
  gaps: number
  blind_hours: number
  points: number
  vessels: number | null
  /** Сколько часов молчания этого потока закрывают другие поставщики (null — не с чем сравнить). */
  blind_hours_closed_by_others: number | null
}

export interface ObsGap {
  start: string
  end: string
  duration_min: number
  bucket: string
  start_lat: number
  start_lon: number
  end_lat: number
  end_lon: number
  implied_kt: number | null
  sources: string[]
  low_confidence: boolean
}

export interface SpeciesItem {
  species_id: number
  name: string
  /** Английское имя из справочника (`fish_species.species_name_english`).
   *  Имена видов — ДАННЫЕ, их правит админка, поэтому оба языка приезжают в
   *  ответе, а не лежат в каталоге витрины. null — английского нет. */
  name_en?: string | null
  /** Код оговорки о статусе модели (`structural_prior`, `unvalidated_upstream`).
   *  Здесь, наоборот, КОД: текст лежит в каталоге витрины
   *  (`species.caveat.<код>`), иначе язык попал бы в кэшируемый ответ.
   *  null — модель проверена, оговаривать нечего. */
  caveat?: string | null
  /** Коды регионов, где вид водится. ПУСТО = виден везде: пустота означает не
   *  «нигде», а «где угодно», иначе новый вид пропадал бы из кабинета до того,
   *  как ему проставят регионы. */
  regions?: string[]
  /** Вид считается по поверхности (`use_depth_model = false`): слайдер глубины
   *  его карту HSI не меняет. */
  surface?: boolean
}

/** Диапазон параметра модели, °C: [от, до]. null — край не задан (а не ноль). */
export interface ThermalBand {
  preferred: [number | null, number | null]
  possible: [number | null, number | null]
}

/** Что модель сравнивает на горизонте шпаргалки. `range` — ключ диапазона
 *  (`sst` или слой `temp_0_50m`); null — ничего, причина в `reason`:
 *  `surface_only` (поверхностная модель), `out_of_depth` (вне глубин вида). */
export interface ThermalHintDepth {
  depth: number
  range: 'sst' | 'temp_0_50m' | 'temp_50_150m' | null
  reason: 'surface_only' | 'out_of_depth' | null
}

export interface ThermalHint {
  species_id: number
  name: string
  name_en?: string | null
  month: number
  /** Месяц строки модели; null — круглогодичная (месячной нет). */
  model_month: number | null
  depth_model: boolean
  /** Один диапазон `sst` сравнивается с температурой ЛЮБОГО горизонта. */
  same_range_all_depths: boolean
  sst: ThermalBand
  layers: Partial<Record<'temp_0_50m' | 'temp_50_150m', ThermalBand>>
  depths: ThermalHintDepth[]
}

export interface ThermalHintsResponse {
  date: string
  month: number
  depths: number[]
  /** Виды без модели на месяц в ответ не попадают — нулей на их месте нет. */
  species: ThermalHint[]
}

/** Регион промысла: география плюс имя. Приезжает ТЕМ ЖЕ ответом, что и виды.
 *
 *  Долгота — восточная непрерывная шкала: `lon_max` свыше 180 означает переход
 *  через дату (Дальний Восток — 118…202), ровно как отдаёт `canonicalBBox`. */
export interface RegionItem {
  code: string
  name: string
  name_en?: string | null
  bbox: { lon_min: number; lon_max: number; lat_min: number; lat_max: number }
}

export interface Cluster {
  /** Право на удаление: автор отметки или владелец кабинета (считает сервер). */
  can_delete?: boolean
  id: number
  lat: number
  lon: number
  species_id: number | null
  size_class: 'small' | 'medium' | 'large' | null
  observed_at: string | null   // ISO 8601 datetime (TZ-aware)
  note: string | null
  created_at: string | null
}

export interface ClusterInput {
  lat: number
  lon: number
  species_id?: number | null
  size_class?: 'small' | 'medium' | 'large' | null
  observed_at?: string | null  // ISO 8601 datetime
  note?: string | null
}

export interface SavedView {
  id: number
  name: string
  url_state: Record<string, unknown>
  created_at: string | null
}

export interface OrgMember {
  user_id: number
  email: string
  role: string
  created_at?: string | null
}

export const DEPTH_LEVELS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 150, 200, 300, 400, 500, 600]
